import * as React from "react"
import { Check, ChevronsUpDown, Plus, Clock, Search } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

const RECENT_INGREDIENTS_KEY = 'tillytics_recent_ingredients';
const MAX_RECENT = 5;

// Get recent ingredients from localStorage
const getRecentIngredients = () => {
  try {
    const stored = localStorage.getItem(RECENT_INGREDIENTS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

// Save ingredient to recent list
const saveToRecent = (ingredientId) => {
  try {
    const recent = getRecentIngredients();
    // Remove if exists, add to front
    const filtered = recent.filter(id => id !== ingredientId);
    const updated = [ingredientId, ...filtered].slice(0, MAX_RECENT);
    localStorage.setItem(RECENT_INGREDIENTS_KEY, JSON.stringify(updated));
  } catch {
    // Ignore localStorage errors
  }
};

export function IngredientCombobox({
  ingredients = [],
  value,
  onSelect,
  onCreateNew,
  placeholder = "Search ingredients...",
  disabled = false,
  className,
  formatCurrency = (val) => `£${(val || 0).toFixed(2)}`,
}) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  // Get selected ingredient details
  const selectedIngredient = React.useMemo(() => 
    ingredients.find(ing => ing.id === value),
    [ingredients, value]
  );

  // Get recent ingredients that exist in current list
  const recentIngredientIds = React.useMemo(() => getRecentIngredients(), []);
  const recentIngredients = React.useMemo(() => 
    recentIngredientIds
      .map(id => ingredients.find(ing => ing.id === id))
      .filter(Boolean)
      .slice(0, 5),
    [recentIngredientIds, ingredients]
  );

  // Filter ingredients based on search
  const filteredIngredients = React.useMemo(() => {
    if (!search) return ingredients;
    const searchLower = search.toLowerCase();
    return ingredients.filter(ing => 
      ing.name.toLowerCase().includes(searchLower) ||
      ing.category?.toLowerCase().includes(searchLower) ||
      ing.supplier_name?.toLowerCase().includes(searchLower)
    );
  }, [ingredients, search]);

  // Group by category for better organization
  const groupedIngredients = React.useMemo(() => {
    const groups = {};
    filteredIngredients.forEach(ing => {
      const cat = ing.category || 'other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(ing);
    });
    return groups;
  }, [filteredIngredients]);

  const handleSelect = (ingredientId) => {
    if (ingredientId === '__create_new__') {
      onCreateNew?.();
    } else {
      saveToRecent(ingredientId);
      onSelect(ingredientId);
    }
    setOpen(false);
    setSearch("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal",
            !value && "text-muted-foreground",
            className
          )}
          data-testid="ingredient-combobox-trigger"
        >
          {selectedIngredient ? (
            <span className="truncate">
              {selectedIngredient.name}
              <span className="ml-2 text-muted-foreground text-xs">
                {formatCurrency(selectedIngredient.cost_per_unit)}/{selectedIngredient.unit}
              </span>
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Search className="w-4 h-4" />
              {placeholder}
            </span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[350px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder="Type to search..." 
            value={search}
            onValueChange={setSearch}
            data-testid="ingredient-search-input"
          />
          <CommandList>
            <CommandEmpty>
              <div className="py-2 text-center">
                <p className="text-sm text-muted-foreground mb-2">No ingredients found</p>
                {onCreateNew && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleSelect('__create_new__')}
                    className="text-green-600"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Create "{search || 'new ingredient'}"
                  </Button>
                )}
              </div>
            </CommandEmpty>

            {/* Create New Option - Always at top */}
            {onCreateNew && (
              <CommandGroup>
                <CommandItem
                  value="__create_new__"
                  onSelect={() => handleSelect('__create_new__')}
                  className="text-green-600 font-medium"
                  data-testid="create-new-ingredient-option"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Create new ingredient...
                </CommandItem>
              </CommandGroup>
            )}

            {/* Recent Ingredients */}
            {!search && recentIngredients.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup heading="Recently Used">
                  {recentIngredients.map((ing) => (
                    <CommandItem
                      key={`recent-${ing.id}`}
                      value={ing.id}
                      onSelect={() => handleSelect(ing.id)}
                      data-testid={`recent-ingredient-${ing.id}`}
                    >
                      <Clock className="mr-2 h-4 w-4 text-muted-foreground" />
                      <span className="flex-1 truncate">{ing.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {formatCurrency(ing.cost_per_unit)}/{ing.unit}
                      </span>
                      {value === ing.id && (
                        <Check className="ml-2 h-4 w-4 text-green-600" />
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}

            {/* All Ingredients (grouped by category when not searching) */}
            {!search ? (
              Object.entries(groupedIngredients)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([category, ings]) => (
                  <React.Fragment key={category}>
                    <CommandSeparator />
                    <CommandGroup heading={category.charAt(0).toUpperCase() + category.slice(1).replace('_', ' ')}>
                      {ings.map((ing) => (
                        <CommandItem
                          key={ing.id}
                          value={ing.id}
                          onSelect={() => handleSelect(ing.id)}
                          data-testid={`ingredient-option-${ing.id}`}
                        >
                          <span className="flex-1 truncate">{ing.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {formatCurrency(ing.cost_per_unit)}/{ing.unit}
                          </span>
                          {value === ing.id && (
                            <Check className="ml-2 h-4 w-4 text-green-600" />
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </React.Fragment>
                ))
            ) : (
              /* Flat list when searching */
              filteredIngredients.length > 0 && (
                <>
                  <CommandSeparator />
                  <CommandGroup heading={`${filteredIngredients.length} result${filteredIngredients.length !== 1 ? 's' : ''}`}>
                    {filteredIngredients.map((ing) => (
                      <CommandItem
                        key={ing.id}
                        value={ing.id}
                        onSelect={() => handleSelect(ing.id)}
                        data-testid={`ingredient-option-${ing.id}`}
                      >
                        <span className="flex-1 truncate">{ing.name}</span>
                        <span className="text-xs text-muted-foreground mr-2">
                          {ing.category}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatCurrency(ing.cost_per_unit)}/{ing.unit}
                        </span>
                        {value === ing.id && (
                          <Check className="ml-2 h-4 w-4 text-green-600" />
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default IngredientCombobox;
